import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";

export type ThresholdTest = {
  id: number;
  sport: string;
  test_date: string; // YYYY-MM-DD
  protocol_code: string;
  status: string; // planned | in_progress | done | aborted
  computed: Record<string, unknown> | null;
  raw_data: Record<string, unknown> | null;
  coach_notes: string | null;
  athlete_notes: string | null;
  created_at: string;
  is_quick_log: boolean;
};

export type ThresholdRequest = {
  id: number;
  protocol_code: string;
  sport: string;
  status: string; // pending | approved | declined | expired | cancelled | consumed
  athlete_notes: string | null;
  coach_notes: string | null;
  requested_at: string;
  decided_at: string | null;
  expires_at: string | null;
  target_date: string | null;
  consumed_test_id: number | null;
};

export type AdvisorDay = {
  date: string;
  score: number;
  weather_score: number;
  weather_summary: Record<string, unknown>;
  flags: string[];
  note: string;
};

export type AdvisorResp = {
  protocol: string;
  sport: string;
  days_since_last: number | null;
  recommended_days: number;
  gap_score: number;
  state_today: number;
  state_breakdown: Record<string, unknown>;
  days: AdvisorDay[];
  best_day: AdvisorDay | null;
  notes: string[];
};

// Vision-audit gap (2026-07-01) — late-stage conditions recheck for an
// already-picked test day. Reuses the advisor's own day scores.
export type RescheduleCheckResp = {
  picked_day: AdvisorDay | null;
  should_reschedule: boolean;
  reason: string | null;
  suggested_day: AdvisorDay | null;
};

export function useRescheduleCheck(requestId: number, enabled: boolean) {
  return useQuery({
    queryKey: ["athlete-threshold-reschedule-check", requestId],
    enabled,
    queryFn: async () => {
      const r = await api.get<RescheduleCheckResp>(
        `/athlete/threshold-tests/requests/${requestId}/reschedule-check`,
      );
      return r.data;
    },
    staleTime: 60 * 60 * 1000,
  });
}

export function useThresholdTests() {
  return useQuery({
    queryKey: ["athlete-threshold-tests"],
    queryFn: async () => {
      const r = await api.get<{ items: ThresholdTest[] }>("/athlete/threshold-tests");
      return r.data.items;
    },
  });
}

export function useThresholdRequests(status?: string) {
  return useQuery({
    queryKey: ["athlete-threshold-test-requests", status ?? null],
    queryFn: async () => {
      const r = await api.get<{ items: ThresholdRequest[] }>(
        "/athlete/threshold-tests/requests",
        { params: status ? { status } : {} },
      );
      return r.data.items;
    },
  });
}

export function useThresholdAdvisor(
  protocol: string,
  sport: string,
  days = 14,
  enabled = true,
) {
  return useQuery({
    queryKey: ["athlete-threshold-advisor", protocol, sport, days],
    enabled,
    queryFn: async () => {
      const r = await api.get<AdvisorResp>("/athlete/threshold-tests/advisor", {
        params: { protocol, sport, days },
      });
      return r.data;
    },
  });
}

export function useCreateRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { protocol_code: string; sport: string; athlete_notes?: string }) => {
      const r = await api.post<ThresholdRequest>("/athlete/threshold-tests/requests", body);
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["athlete-threshold-test-requests"] });
    },
  });
}

export function useCancelRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (request_id: number) => {
      const r = await api.post<ThresholdRequest>(
        `/athlete/threshold-tests/requests/${request_id}/cancel`,
      );
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["athlete-threshold-test-requests"] });
    },
  });
}

export type DirectEntryPayload = {
  protocol_code: string;
  sport: string;
  test_date: string;
  raw_data: Record<string, unknown>;
  athlete_notes?: string;
  is_quick_log?: boolean;
  confirm_apply_zones?: boolean;
  location_id?: number;
};

export function useDirectSubmit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: DirectEntryPayload) => {
      const r = await api.post<ThresholdTest>("/athlete/threshold-tests/direct", body);
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["athlete-threshold-tests"] });
      qc.invalidateQueries({ queryKey: ["athlete-zones"] });
    },
  });
}

export type UpdateTestPayload = {
  test_date?: string;
  raw_data?: Record<string, unknown>;
  athlete_notes?: string;
};

export function useUpdateTest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: UpdateTestPayload & { id: number }) => {
      const r = await api.patch<ThresholdTest>(`/athlete/threshold-tests/${id}`, body);
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["athlete-threshold-tests"] });
      qc.invalidateQueries({ queryKey: ["athlete-zones"] });
    },
  });
}

export function useDeleteTest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/athlete/threshold-tests/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["athlete-threshold-tests"] });
    },
  });
}

export function usePickDay() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, target_date }: { id: number; target_date: string }) => {
      const r = await api.post<ThresholdRequest>(
        `/athlete/threshold-tests/requests/${id}/pick-day`,
        { target_date },
      );
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["athlete-threshold-test-requests"] });
    },
  });
}

export type WeatherDay = {
  date: string;
  temp_max_c: number | null;
  temp_min_c: number | null;
  precipitation_probability_max: number | null;
  wind_speed_max: number | null;
  uv_index_max: number | null;
  weather_code: number | null;
};

/**
 * Прогноз на дату по координатам атлета.
 *
 * Имя `useTestDayWeather` осталось от точки, где расчёт впервые
 * понадобился — подбора дня для теста, — но сам эндпоинт ничего о тестах
 * не знает. Лист дня плана спрашивает ТОТ ЖЕ прогноз, и заводить второй
 * запрос ради нейтрального имени значило бы дважды ходить за одним
 * ответом. Алиас ниже даёт честное имя, разделяя ключ кеша.
 */
export function useTestDayWeather(date: string | null) {
  return useQuery<WeatherDay | null>({
    queryKey: ["test-day-weather", date],
    queryFn: async () => {
      if (!date) return null;
      const r = await api.get<WeatherDay | null>(`/athlete/threshold-tests/weather?date=${date}`);
      return r.data ?? null;
    },
    enabled: !!date,
    staleTime: 30 * 60 * 1000,
  });
}

// ─── DFA alpha-1 (P6 — experimental, non-invasive HRV threshold estimate) ────

export type DFAAlphaPoint = { t_sec: number; alpha1: number };

export type RRSessionOut = {
  id: number;
  threshold_test_id: number | null;
  source_app: string;
  recorded_at: string;
  duration_sec: number;
  rr_count: number;
  dfa_alpha1_series: DFAAlphaPoint[] | null;
  dfa_alpha1_thresholds: { vt1_alpha1_crossing_t_sec: number | null; vt2_alpha1_crossing_t_sec: number | null } | null;
  quality_flags: { ppg_suspected: boolean; series_points: number } | null;
  experimental: boolean;
  warnings: string[];
};

export function useUploadRRSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { file: File; sourceApp: string; thresholdTestId?: number }) => {
      const fd = new FormData();
      fd.append("file", vars.file);
      fd.append("source_app", vars.sourceApp);
      if (vars.thresholdTestId != null) fd.append("threshold_test_id", String(vars.thresholdTestId));
      const r = await api.post<RRSessionOut>(
        "/athlete/threshold-tests/rr-upload",
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["athlete-rr-sessions"] });
    },
  });
}

export function useRRSessions() {
  return useQuery<RRSessionOut[]>({
    queryKey: ["athlete-rr-sessions"],
    queryFn: async () => {
      const r = await api.get<RRSessionOut[]>("/athlete/threshold-tests/rr-sessions");
      return r.data;
    },
  });
}

// ─── P7 — saved test locations ────────────────────────────────────────────

export type TestLocation = {
  id: number;
  label: string;
  lat: number;
  lon: number;
  altitude_m: number | null;
  environment: string | null;
  surface: string | null;
  is_default: boolean;
  notes: string | null;
};

export type TestLocationInput = {
  label: string;
  lat: number;
  lon: number;
  altitude_m?: number | null;
  environment?: string | null;
  surface?: string | null;
  is_default?: boolean;
  notes?: string | null;
};

export function useTestLocations() {
  return useQuery<TestLocation[]>({
    queryKey: ["athlete-test-locations"],
    queryFn: async () => {
      const r = await api.get<TestLocation[]>("/athlete/threshold-tests/locations");
      return r.data;
    },
  });
}

export function useCreateTestLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: TestLocationInput) => {
      const r = await api.post<TestLocation>("/athlete/threshold-tests/locations", body);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["athlete-test-locations"] }),
  });
}

export function useDeleteTestLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/athlete/threshold-tests/locations/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["athlete-test-locations"] }),
  });
}

/** Тот же прогноз под нейтральным именем — для листа дня плана. */
export const useDayForecast = useTestDayWeather;
